import { Bad } from '@core/model/framework/Result/BadResult';
import {
  WaitOnCaseStateNotificationGateway,
  WaitOnCaseStateNotificationGatewayInput,
  WaitOnCaseStateNotificationGatewayOutput,
} from '@core/model/gateway/waitOnCaseStateNotificationGateway';
import type {
  AccountService,
  GetCaseStateServiceInput,
  GetCaseStateServiceOutput,
} from '@core/model/service/accountService';
import { SERVICE_ID } from '@di/serviceId';
import { inject, injectable } from 'inversify';
import { BehaviorSubject, TimeoutError } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import { from } from 'rxjs/internal/observable/from';
import { Result } from '@core/model/framework/Result/Result';
import { Ok } from '@core/model/framework/Result/OkResult';
import type { Logger } from '@core/model/domain/logger';
import { poll } from '../../../model/framework/notifications/polling';

@injectable()
export class WaitOnCaseStateNotificationViaPolling implements WaitOnCaseStateNotificationGateway {
  private manualForceStopPolling$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    @inject(SERVICE_ID.accountService)
    private readonly accountService: AccountService,
    @inject(SERVICE_ID.waitOnCaseStateTimeoutInSeconds)
    private readonly timeoutInSeconds: number,
    @inject(SERVICE_ID.waitOnCaseStateIntervalInSeconds)
    private readonly intervalInSeconds: number,
    @inject(SERVICE_ID.logger)
    private readonly logger: Logger
  ) {}

  async awaitCaseStateUpdate(
    input: WaitOnCaseStateNotificationGatewayInput
  ): WaitOnCaseStateNotificationGatewayOutput {
    try {
      const caseStateResult = await firstValueFrom(
        poll<WaitOnCaseStateNotificationGatewayInput, Awaited<GetCaseStateServiceOutput>>({
          intervalInSeconds: this.intervalInSeconds,
          timeoutInSeconds: this.timeoutInSeconds,
          apiCall: () =>
            this.getCaseStateAsObservable({
              userId: input.userId,
              userToken: input.userToken,
            }),
          shouldContinuePolling: (caseState) => this.shouldContinuePolling(caseState),
          manualForceStop: this.manualForceStopPolling$,
          logger: this.logger,
          pollingName: 'CASE_STATE_UPDATE',
        })
      );

      const accountAnalysisState = Result.unwrap(caseStateResult);
      return Ok(accountAnalysisState);
    } catch (e) {
      if (e instanceof TimeoutError) {
        return Bad('CASE_IN_INCORRECT_STATUS');
      } else if (e === 'STOPPED_POLLING_VIA_USER_ACTION') {
        this.manualForceStopPolling$.next(false);
        return Bad('STOPPED_POLLING_VIA_USER_ACTION');
      }

      this.logger.logException(e);
      throw e;
    }
  }

  stopWaitingCaseStateUpdate(): void {
    this.manualForceStopPolling$.next(true);
  }

  private getCaseStateAsObservable = (
    input: GetCaseStateServiceInput
  ): Observable<Awaited<GetCaseStateServiceOutput>> => {
    return new Observable((observer) => {
      from(
        this.accountService.getCaseState({
          userId: input.userId,
          userToken: input.userToken,
        })
      ).subscribe({
        next: (result) => {
          observer.next(result);
        },
      });
    });
  };

  private shouldContinuePolling = (caseState: Awaited<GetCaseStateServiceOutput>): boolean => {
    if (caseState.success === false) {
      return true;
    }

    if (caseState.value === 'IN_PROGRESS') {
      return true;
    }

    return false;
  };
}
